import type { Hint } from "~/features/chat/types";
import type { TermMatch, TermMatcher } from "~/utils/termMatcher";

/**
 * Shared term-matching primitives used by both the v1 markdown-string
 * highlighter (`useTermHighlighting`) and the v2 MDAST/HAST term-highlight pass
 * (`rehypeTermHighlight`).
 *
 * Keeping these in one module guarantees the two renderers use the **same**
 * match detection and merge/priority algorithm (AI term wins on overlap), so a
 * given span highlights identically and opens the same term definition
 * regardless of which renderer is active.
 */

/** Class applied to every highlighted term `<mark>`. */
export const TERM_HIGHLIGHT_CLASS = "term-highlight";

/**
 * Attribute carrying the normalized lookup key that `TermDefinitionDialog`
 * reads to open the matching definition.
 */
export const TERM_KEY_ATTRIBUTE = "data-term-key";

/**
 * Normalizes a matched term into the `data-term-key` value. Matches are already
 * lower-cased by both matchers; this keeps the normalization in one place so
 * v1 and v2 stay identical.
 */
export function normalizeTermKey(term: string): string {
  return term.toLowerCase();
}

/**
 * Escapes special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Builds a single combined regex from all AI terms for efficient matching.
 * Terms are sorted by length (longest first) to ensure longer phrases match
 * before shorter substrings. Returns `null` when there are no terms.
 */
export function buildTermRegex(terms: Hint[]): RegExp | null {
  if (!terms.length) return null;

  const sorted = [...terms].sort((a, b) => b.term.length - a.term.length);
  const pattern = sorted.map((t) => escapeRegex(t.term)).join("|");
  return new RegExp(`\\b(${pattern})\\b`, "gi");
}

/**
 * Finds all regex (AI-term) matches in a text segment, returning position-based
 * results. Terms are lower-cased into the match `term` field.
 */
export function findRegexMatches(text: string, regex: RegExp): TermMatch[] {
  const matches: TermMatch[] = [];
  regex.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    if (m[0].length === 0) {
      regex.lastIndex++;
      continue;
    }
    matches.push({
      start: m.index,
      end: m.index + m[0].length,
      term: m[0].toLowerCase(),
    });
  }
  return matches;
}

/**
 * Merges AI-term and medication matches, preferring AI terms over medications
 * when they overlap. Returns non-overlapping matches sorted by position.
 */
export function mergeMatches(
  aiMatches: TermMatch[],
  medMatches: TermMatch[]
): TermMatch[] {
  const combined = [...aiMatches, ...medMatches];
  combined.sort((a, b) => a.start - b.start || b.end - a.end);

  // Mark AI matches with priority — when overlapping, AI wins.
  const aiSet = new Set(aiMatches);
  const result: TermMatch[] = [];
  let lastEnd = 0;

  for (const m of combined) {
    if (m.start >= lastEnd) {
      result.push(m);
      lastEnd = m.end;
    } else if (aiSet.has(m) && m.start < lastEnd) {
      // AI match overlaps a previously accepted medication match — replace it.
      if (result.length > 0 && !aiSet.has(result[result.length - 1])) {
        result[result.length - 1] = m;
        lastEnd = m.end;
      }
    }
  }

  return result;
}

/**
 * Computes the non-overlapping term matches for a single text segment using the
 * AI-term regex and the medication matcher, applying the shared AI-over-medication
 * merge priority. Returns an empty array when nothing matches.
 */
export function computeTermMatches(
  text: string,
  regex: RegExp | null,
  matcher: TermMatcher | null
): TermMatch[] {
  const aiMatches = regex ? findRegexMatches(text, regex) : [];
  const medMatches = matcher ? matcher.search(text) : [];

  if (!aiMatches.length && !medMatches.length) return [];

  return mergeMatches(aiMatches, medMatches);
}
